export interface DashboardThresholds {
  firstResponseTime: {
    target: number; // minutes
    warning: number; // minutes
    critical: number; // minutes
  };
  resolutionRates: {
    target: number; // percentage
    warning: number; // percentage
    critical: number; // percentage
  };
  agingTickets: {
    criticalAge: number; // hours
    highAge: number; // hours
    mediumAge: number; // hours
    lowAge: number; // hours
  };
  hotspots: {
    criticalThreshold: number; // count
    highThreshold: number; // count
    mediumThreshold: number; // count
  };
}

export interface DashboardMetrics {
  refreshInterval: number; // milliseconds
  autoRefresh: boolean;
  chartAnimationDuration: number; // milliseconds
  maxDataPoints: number;
}

export const DEFAULT_THRESHOLDS: DashboardThresholds = {
  firstResponseTime: {
    target: 30, // 30 minutes
    warning: 45, // 45 minutes
    critical: 60, // 60 minutes
  },
  resolutionRates: {
    target: 90, // 90%
    warning: 75, // 75%
    critical: 60, // 60%
  },
  agingTickets: {
    criticalAge: 24, // 24 hours
    highAge: 48, // 48 hours
    mediumAge: 72, // 72 hours
    lowAge: 168, // 1 week
  },
  hotspots: {
    criticalThreshold: 10,
    highThreshold: 5,
    mediumThreshold: 3,
  },
};

export const DEFAULT_METRICS: DashboardMetrics = {
  refreshInterval: 30000, // 30 seconds
  autoRefresh: true,
  chartAnimationDuration: 500,
  maxDataPoints: 100,
};

// Color schemes for different priority levels
export const PRIORITY_COLORS = {
  critical: '#ef4444', // red
  high: '#f97316', // orange
  medium: '#eab308', // yellow
  low: '#22c55e', // green
};

// Color schemes for different status levels
export const STATUS_COLORS = {
  completed: '#22c55e', // green
  in_progress: '#3b82f6', // blue
  assigned: '#eab308', // yellow
  pending: '#6b7280', // gray
  cancelled: '#ef4444', // red
};

// Department colors for consistent visualization
export const DEPARTMENT_COLORS = {
  'Electricity': '#3b82f6', // blue
  'Water': '#06b6d4', // cyan
  'Roads': '#f97316', // orange
  'Waste': '#8b5cf6', // purple
  'Sanitation': '#10b981', // green
  'Parks': '#84cc16', // lime
  'Traffic': '#f59e0b', // amber
  'Building': '#6b7280', // gray
};

// Timeframe options for filtering
export const TIMEFRAME_OPTIONS = [
  { value: '24h', label: 'Last 24 Hours', hours: 24 },
  { value: '7d', label: 'Last 7 Days', hours: 168 },
  { value: '30d', label: 'Last 30 Days', hours: 720 },
  { value: '90d', label: 'Last 90 Days', hours: 2160 },
];

// Chart types available for different metrics
export const CHART_TYPES = {
  firstResponseTime: ['line', 'bar'],
  agingTickets: ['pie', 'bar'],
  resolutionRates: ['line', 'bar', 'progress'],
  hotspots: ['bar', 'pie'],
  trends: ['line', 'area'],
};

// Export formats
export const EXPORT_FORMATS = [
  { value: 'excel', label: 'Excel (.xlsx)', icon: '📊' },
  { value: 'pdf', label: 'PDF Report', icon: '📄' },
  { value: 'csv', label: 'CSV Data', icon: '📋' },
  { value: 'json', label: 'JSON Data', icon: '🔧' },
];

// Notification settings
export const NOTIFICATION_SETTINGS = {
  criticalThreshold: 5, // minutes
  warningThreshold: 15, // minutes
  updateInterval: 10000, // 10 seconds
  maxNotifications: 10,
};

// Performance indicators
export const PERFORMANCE_INDICATORS = {
  excellent: { min: 90, color: '#22c55e', label: 'Excellent' },
  good: { min: 75, color: '#10b981', label: 'Good' },
  fair: { min: 60, color: '#eab308', label: 'Fair' },
  poor: { min: 0, color: '#ef4444', label: 'Poor' },
};

// Get color for performance level
export const getPerformanceColor = (value: number): string => {
  if (value >= PERFORMANCE_INDICATORS.excellent.min) return PERFORMANCE_INDICATORS.excellent.color;
  if (value >= PERFORMANCE_INDICATORS.good.min) return PERFORMANCE_INDICATORS.good.color;
  if (value >= PERFORMANCE_INDICATORS.fair.min) return PERFORMANCE_INDICATORS.fair.color;
  return PERFORMANCE_INDICATORS.poor.color;
};

// Get performance label
export const getPerformanceLabel = (value: number): string => {
  if (value >= PERFORMANCE_INDICATORS.excellent.min) return PERFORMANCE_INDICATORS.excellent.label;
  if (value >= PERFORMANCE_INDICATORS.good.min) return PERFORMANCE_INDICATORS.good.label;
  if (value >= PERFORMANCE_INDICATORS.fair.min) return PERFORMANCE_INDICATORS.fair.label;
  return PERFORMANCE_INDICATORS.poor.label;
};

// Get priority color
export const getPriorityColor = (priority: string): string => {
  return PRIORITY_COLORS[priority.toLowerCase() as keyof typeof PRIORITY_COLORS] || PRIORITY_COLORS.medium;
};

// Get status color
export const getStatusColor = (status: string): string => {
  return STATUS_COLORS[status.toLowerCase() as keyof typeof STATUS_COLORS] || STATUS_COLORS.pending;
};

// Get department color
export const getDepartmentColor = (department: string): string => {
  return DEPARTMENT_COLORS[department as keyof typeof DEPARTMENT_COLORS] || '#6b7280';
};





